import type { DrillId } from "../content/drills";
import { PHRASES } from "../content/phrases";
import { TWISTERS } from "../content/twisters";
import type { Card, Lang } from "../content/types";
import type { Profile, SrsCard } from "../state/profile";
import { mulberry32 } from "./clarity";
import { MASTERY_INTERVAL_DAYS } from "./mastery";
import { dueCards } from "./srs";
import { weakestPhonemes } from "./soundmap";

export type SessionType = "daily" | "twisters" | "fluency" | "weak" | "free";

export type SessionItem =
  | { kind: "drill"; drillId: DrillId }
  | { kind: "twister"; cardId: string; tempo: "slow" | "steady" | "fast" }
  | { kind: "phrase"; cardId: string; mode: "rep" | "recall" };

const ALL_CARDS: Card[] = [...TWISTERS, ...PHRASES];
const CARD_BY_ID = new Map(ALL_CARDS.map((c) => [c.id, c]));

function shuffled<T>(arr: T[], rng: () => number): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = a[i]!;
    a[i] = a[j]!;
    a[j] = tmp;
  }
  return a;
}

function poolForLangs(cards: Card[], langs: Lang[]): Card[] {
  return cards.filter((c) => langs.includes(c.lang));
}

function tempoForCard(card: SrsCard | undefined): "slow" | "steady" | "fast" {
  if (!card || card.interval < 3) return "slow";
  if (card.interval < MASTERY_INTERVAL_DAYS) return "steady";
  return "fast";
}

function modeForCard(card: SrsCard | undefined): "rep" | "recall" {
  return !card || card.reps === 0 ? "rep" : "recall";
}

function itemForCard(content: Card, card: SrsCard | undefined): SessionItem {
  return content.kind === "twister"
    ? { kind: "twister", cardId: content.id, tempo: tempoForCard(card) }
    : { kind: "phrase", cardId: content.id, mode: modeForCard(card) };
}

function dueCardIds(profile: Profile, today: string, langs: Lang[]): string[] {
  return dueCards(profile, today).filter((id) => langs.includes(CARD_BY_ID.get(id)?.lang ?? "en"));
}

function newCardIds(profile: Profile, langs: Lang[], rng: () => number, count: number, pool: Card[] = ALL_CARDS): string[] {
  const untouched = poolForLangs(pool, langs).filter((c) => !profile.cards[c.id]);
  return shuffled(untouched, rng)
    .slice(0, count)
    .map((c) => c.id);
}

function sessionLengthCounts(profile: Profile): { reviewCap: number; newPerDay: number } {
  const lengthMult = profile.settings.sessionLength === "short" ? 0.6 : profile.settings.sessionLength === "long" ? 1.4 : 1;
  return {
    reviewCap: Math.max(1, Math.round(profile.settings.reviewCap * lengthMult)),
    newPerDay: profile.settings.newPerDay,
  };
}

function hashSeed(seed: number, today: string, salt: string): number {
  let h = seed >>> 0;
  const s = `${today}|${salt}`;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 2654435761) >>> 0;
  return h >>> 0;
}

function warmupDrillId(rng: () => number): DrillId {
  return rng() < 0.5 ? "vowel" : "breath";
}

function buildDaily(profile: Profile, today: string, langs: Lang[], rng: () => number): SessionItem[] {
  const items: SessionItem[] = [{ kind: "drill", drillId: warmupDrillId(rng) }];

  const { reviewCap } = sessionLengthCounts(profile);
  const due = dueCardIds(profile, today, langs).slice(0, Math.max(0, reviewCap - 2)); // room for 1 new + 1 finisher
  for (const id of due) {
    const content = CARD_BY_ID.get(id);
    if (content) items.push(itemForCard(content, profile.cards[id]));
  }

  const [newId] = newCardIds(profile, langs, rng, 1);
  if (newId) items.push(itemForCard(CARD_BY_ID.get(newId)!, undefined));

  const finisherPool = shuffled(poolForLangs(TWISTERS, langs), rng).sort((a, b) => (b.level ?? 1) - (a.level ?? 1));
  const finisher = finisherPool[0];
  if (finisher) {
    items.push({ kind: "twister", cardId: finisher.id, tempo: "fast" });
  } else {
    const phrase = shuffled(poolForLangs(PHRASES, langs), rng)[0];
    if (phrase) items.push({ kind: "phrase", cardId: phrase.id, mode: "recall" });
  }

  return items;
}

function buildCardFocused(
  profile: Profile,
  today: string,
  langs: Lang[],
  rng: () => number,
  kind: "twister" | "phrase",
): SessionItem[] {
  const { reviewCap, newPerDay } = sessionLengthCounts(profile);
  const contentPool = kind === "twister" ? TWISTERS : PHRASES;
  const due = dueCardIds(profile, today, langs)
    .filter((id) => CARD_BY_ID.get(id)?.kind === kind)
    .slice(0, reviewCap);
  const items: SessionItem[] = due.map((id) => itemForCard(CARD_BY_ID.get(id)!, profile.cards[id]));

  const fresh = newCardIds(profile, langs, rng, newPerDay, contentPool);
  for (const id of fresh) items.push(itemForCard(CARD_BY_ID.get(id)!, undefined));

  if (items.length) return items;
  // fresh profile / nothing due yet and nothing new drawn — sample the pool so a session is never empty
  return shuffled(poolForLangs(contentPool, langs), rng)
    .slice(0, 5)
    .map((c) => itemForCard(c, undefined));
}

function buildWeak(profile: Profile, _today: string, langs: Lang[], rng: () => number): SessionItem[] {
  const weak = weakestPhonemes(profile, 3);
  const targeted = poolForLangs(TWISTERS, langs).filter((t) => t.phonemes.some((ph) => weak.includes(ph)));
  const pool = targeted.length ? targeted : poolForLangs(TWISTERS, langs);
  return shuffled(pool, rng)
    .slice(0, 5)
    .map((t) => itemForCard(t, profile.cards[t.id]));
}

function buildFree(profile: Profile, today: string, langs: Lang[], rng: () => number): SessionItem[] {
  const { reviewCap, newPerDay } = sessionLengthCounts(profile);
  const due = dueCardIds(profile, today, langs).slice(0, reviewCap);
  const items = due.map((id) => itemForCard(CARD_BY_ID.get(id)!, profile.cards[id]));

  const fresh = newCardIds(profile, langs, rng, newPerDay);
  for (const id of fresh) items.push(itemForCard(CARD_BY_ID.get(id)!, undefined));

  if (items.length) return items;
  return shuffled(poolForLangs(ALL_CARDS, langs), rng)
    .slice(0, 6)
    .map((c) => itemForCard(c, undefined));
}

/** Deterministic given (profile.seed, today, type) — a reload rebuilds the
    exact same session, which is what makes mid-session Resume possible. */
export function buildSession(profile: Profile, type: SessionType, today: string): SessionItem[] {
  const langs = profile.identity.langs.length ? profile.identity.langs : (["en"] as Lang[]);
  const rng = mulberry32(hashSeed(profile.seed, today, type));

  switch (type) {
    case "daily":
      return buildDaily(profile, today, langs, rng);
    case "twisters":
      return buildCardFocused(profile, today, langs, rng, "twister");
    case "fluency":
      return buildCardFocused(profile, today, langs, rng, "phrase");
    case "weak":
      return buildWeak(profile, today, langs, rng);
    case "free":
      return buildFree(profile, today, langs, rng);
  }
}
